import { Project } from '../types/project';

// Helper function to generate image paths from a folder
const generateProjectImages = (folderPath: string, count: number): string[] => {
  return Array.from({ length: count }, (_, i) => {
    const num = String(i + 1).padStart(3, '0');
    return `${folderPath}/${num}.webp`;
  });
};

export const projectsData: Project[] = [
  {
    id: "01",
    slug: "in-the-footsteps-of-memory",
    title: {
      en: "In the footsteps of memory.",
      it: "Sulle tracce del ricordo."
    },
    subtitle: {
      en: "Reuse of secondary electrical cabins along the Via Francigena",
      it: "Percorso di riuso delle cabine elettriche secondarie lungo la Via Francigena"
    },
    description: {
      en: "Ancient pilgrimage routes are of great importance to Italy's cultural heritage, as are consular roads and minor historic roads, whose enhancement should form an integral part of a broader plan to restore the country's historical, artistic and landscape heritage.",
      it: "Nell'ambito del patrimonio culturale italiano, grande importanza rivestono gli antichi percorsi dei pellegrini, così come le strade consolari e la viabilità storica minore, la cui valorizzazione dovrebbe costituire parte integrante di un più ampio piano di recupero del patrimonio storico, artistico e paesaggistico."
    },
    category: {
      en: "Architecture",
      it: "Architettura"
    },
    details: [
      { label: { en: "Year", it: "Anno" }, value: "2023" },
      { label: { en: "Role", it: "Ruolo" }, value: { en: "Student (Thesis)", it: "Studente (Tesi di Laurea)" } },
      { label: { en: "Location", it: "Location" }, value: "San Gimignano, IT" },
      { label: { en: "Typology", it: "Tipologia" }, value: { en: "Adaptive Reuse / Heritage", it: "Riuso Adattivo / Patrimonio" } },
      { label: { en: "Institution", it: "Istituzione" }, value: { en: "University of Florence (DIDA)", it: "Università degli Studi di Firenze (DIDA)" } }
    ],
    images: generateProjectImages('/001', 4),
    heroImage: '/001/001.webp',
    link: "/project/01",
    challenge: {
      en: "The electrical distribution infrastructure in rural Tuscany left dozens of decommissioned masonry substations stranded across iconic rolling landscapes. These silent towers, situated right by the historic Via Francigena, risk progressive decay despite their strategic territorial distribution.",
      it: "L'infrastruttura di distribuzione elettrica nella Toscana rurale ha lasciato decine di cabine in muratura dismesse, isolate nel paesaggio collinare. Queste torri silenziose, situate lungo il tracciato storico della Via Francigena, rischiano un progressivo degrado nonostante la loro strategica collocazione territoriale."
    },
    strategy: {
      en: "The project conceives a network of micro-interventions using reversible lightweight steel and timber joinery. Each cabin is retrofitted into an active resting station providing water harvesting, shade, contemplative lookouts over the Tuscan hills, and an interpretive spatial narrative of pilgrimage memory.",
      it: "Il progetto concepisce una rete di micro-interventi mediante strutture reversibili e leggere in legno e acciaio. Ogni cabina viene convertita in stazione di sosta attiva: raccolta delle acque piovane, ombra, belvedere contemplativi sulle colline toscane e un percorso narrativo e memoriale sul pellegrinaggio."
    },
    sections: [
      {
        title: { en: "Territorial Context & Pilgrimage Network", it: "Contesto Territoriale e Rete dei Cammini" },
        subtitle: { en: "Connecting landscape, infrastructure and slow mobility", it: "Connettere paesaggio, infrastruttura e mobilità lenta" },
        content: {
          en: "The Via Francigena represents an extraordinary matrix connecting cultural nodes across the Italian peninsula. By surveying decommissioned ENEL electrical substations along the leg near San Gimignano, the study identifies an untapped territorial network capable of reviving slow tourism without consuming new soil.",
          it: "La Via Francigena rappresenta una straordinaria matrice di collegamento tra presidi culturali della penisola italiana. Attraverso il rilievo e la mappatura delle cabine ENEL dismesse lungo il tratto di San Gimignano, la ricerca individua una rete territoriale latente in grado di potenziare il turismo lento senza alcun consumo di suolo."
        },
        image: '/001/002.webp',
        imageCaption: {
          en: "Territorial mapping, section studies and structural adaptation of the tower typology",
          it: "Mappatura territoriale, sezioni di studio e adattamento strutturale della tipologia a torre"
        }
      },
      {
        title: { en: "Architectural Tectonic & Materiality", it: "Tettonica Architettonica e Matericità" },
        subtitle: { en: "Dialogue between historical masonry and dry construction", it: "Dialogo tra muratura storica e montaggio a secco" },
        content: {
          en: "The existing brick envelope is respected as a historical artifact, preserving its monolithic volume. A light inner timber scaffolding is inserted to accommodate vertical circulation, observation decks, and shaded resting niches, maintaining a clear visual distinction between original and contemporary materials.",
          it: "L'involucro in laterizio esistente è rispettato come manufatto storico, conservandone il volume monolitico. All'interno si innesta una struttura a secco in legno per la risalita verticale, terrazze di osservazione e nicchie d'ombra, mantenendo una netta leggibilità tra preesistenza e innesto contemporaneo."
        },
        image: '/001/003.webp',
        imageCaption: {
          en: "Detailed architectural drawings, plan layouts and environmental integration",
          it: "Elaborati grafici esecutivi, piante di distribuzione e integrazione ambientale"
        }
      },
      {
        title: { en: "Atmosphere & Spatial Experience", it: "Atmosfera ed Esperienza Spaziale" },
        subtitle: { en: "A sanctuary for contemplation and rest", it: "Un santuario per la sosta e la meditazione" },
        content: {
          en: "Controlled openings and apertures frame selective views of the Tuscan landscape, modulating natural light and casting dramatic shadows inside the high-ceilinged void. The experience re-establishes an intimate dialogue between traveler, architectural shelter, and nature.",
          it: "Aperture calibrate inquadrano viste selezionate della campagna toscana, modulando la luce zenitale e creando suggestivi chiaroscuri nel vuoto verticale. L'esperienza ristabilisce un dialogo intimo tra viaggiatore, riparo architettonico e paesaggio."
        },
        image: '/001/004.webp',
        imageCaption: {
          en: "Atmospheric perspective render of the renovated interior lookout space",
          it: "Render prospettico dell'interno riqualificato con vista panoramica"
        }
      }
    ]
  },
  {
    id: "02",
    slug: "zero-six-zero-sixteen-zero-sixty",
    title: {
      en: "Zero-Six, Zero-Sixteen, Zero-Sixty",
      it: "Zero-Six, Zero-Sixteen, Zero-Sixty"
    },
    subtitle: {
      en: "Intergenerational Community Hub & Creative Learning Spaces",
      it: "Hub di comunità intergenerazionale e spazi di apprendimento creativo"
    },
    description: {
      en: "Evolving from the Zero-Six request, the project created the versatile Zero-Sixty. Three age flows are arranged with minimal overlap through thoughtful layout and timing. The main hall hosts visitors and workshops, while the crypt offers creative learning spaces.",
      it: "Evolutosi dalla richiesta Zero-Six, il progetto ha dato vita al versatile Zero-Sixty. Tre flussi di età sono organizzati con una sovrapposizione minima grazie a una disposizione e una tempistica accuratamente studiate. La sala principale ospita visitatori e workshop, mentre la cripta offre spazi creativi per l'apprendimento."
    },
    category: {
      en: "Restoration / Interior Design",
      it: "Restauro / Design Interno"
    },
    details: [
      { label: { en: "Year", it: "Anno" }, value: "2024" },
      { label: { en: "Role", it: "Ruolo" }, value: { en: "Student", it: "Studente" } },
      { label: { en: "Location", it: "Location" }, value: "Torino, IT" },
      { label: { en: "Typology", it: "Tipologia" }, value: { en: "Heritage Adaptive Reuse / Education", it: "Riuso Patrimonio Storico / Educativo" } },
      { label: { en: "Institution", it: "Istituzione" }, value: { en: "Polytechnic University of Turin", it: "Politecnico di Torino" } }
    ],
    images: generateProjectImages('/002', 6),
    heroImage: '/002/001.webp',
    link: "/project/02",
    challenge: {
      en: "Accommodating diverse age cohorts—infants (0-6), adolescents (0-16), and elderly community members (60+)—within a sensitive historical complex without creating physical segregation or acoustic interference.",
      it: "Accogliere fasce d'età differenti—prima infanzia (0-6), adolescenti (0-16) e anziani della comunità (60+)—all'interno di un complesso storico vincolato senza generare segregazione spaziale o interferenze acustiche."
    },
    strategy: {
      en: "A diurnal flow schedule and flexible modular partition system allow spaces to shift configurations across morning, afternoon, and evening. The crypt becomes an immersive acoustics-controlled maker lab, while the vaulted nave acts as an open agora.",
      it: "Un programma orario di flussi diurni e un sistema di partizioni modulari consentono agli spazi di rimodularsi tra mattino, pomeriggio e sera. La cripta diventa un laboratorio esperienziale per la creatività, mentre la navata a volte funge da agora civica aperta."
    },
    sections: [
      {
        title: { en: "Adaptive Strategy & Program Distribution", it: "Strategia Adattiva e Programma Funzionale" },
        subtitle: { en: "Harmonizing community activities through spatial choreography", it: "Armonizzare le attività comunitarie attraverso una coreografia spaziale" },
        content: {
          en: "The historic complex in Turin features monumental masonry and vaulted ceilings. Rather than introducing permanent subdividing walls, lightweight furniture and reversible curtains define adaptable micro-zones for reading, digital fabrication, storytelling, and collective dining.",
          it: "L'edificio storico torinese si distingue per le sue murature monumentali e le grandi volte. Piuttosto che introdurre pareti divisorie fisse, arredi leggeri e tendaggi acustici definiscono micro-ambienti versatili per la lettura, la fabbricazione digitale, il racconto e i momenti conviviali."
        },
        image: '/002/002.webp',
        imageCaption: {
          en: "Floor plans displaying fluid circulation patterns and age group zoning",
          it: "Piante architettoniche con diagrammi di circolazione e zonizzazione per fasce d'età"
        }
      },
      {
        title: { en: "Crypt Activation & Atmospheric Lighting", it: "Attivazione della Cripta e Progetto della Luce" },
        subtitle: { en: "Reclaiming the subterranean realm for tactile discovery", it: "Recuperare il piano ipogeo per l'esplorazione sensoriale" },
        content: {
          en: "The subterranean crypt level is reconditioned into a sensory workshop. Indirect lighting washes across the historic brick vaults, protecting historic textures while creating a secure, warm environment suited for hands-on pedagogical workshops.",
          it: "Il piano ipogeo della cripta viene rigenerato in laboratorio sensoriale. L'illuminazione indiretta esalta la matericità del mattone storico, garantendo un'atmosfera protetta e calda adatta ad attività laboratoriali e pedagogiche."
        },
        image: '/002/003.webp',
        imageCaption: {
          en: "Transverse sections detailing light wells and underground environmental systems",
          it: "Sezioni trasversali con pozzi di luce e integrazione impiantistica ipogea"
        }
      }
    ]
  },
  {
    id: "03",
    slug: "maternity-center-kaira-looro",
    title: {
      en: "Maternity Center, Kaira Looro",
      it: "Centro Maternità, Kaira Looro"
    },
    subtitle: {
      en: "Sustainable Healthcare Architecture in Casamance",
      it: "Architettura sanitaria sostenibile in Casamance"
    },
    description: {
      en: "In Casamance, Senegal, a maternity center blends tradition and modern care through eco-friendly materials like earth bricks and bamboo. Curved lines and open patios create cool, welcoming spaces that balance privacy, community, and a sense of calm for mothers.",
      it: "A Casamance, in Senegal, un centro maternità fonde tradizione e cure moderne attraverso materiali ecologici come mattoni di terra e bambù. Le linee curve e i patii aperti creano spazi freschi e accoglienti che garantiscono alle mamme privacy, senso di comunità e tranquillità."
    },
    category: {
      en: "Design Competition",
      it: "Competizione di Design"
    },
    details: [
      { label: { en: "Year", it: "Anno" }, value: "2024" },
      { label: { en: "Role", it: "Ruolo" }, value: { en: "Student (Competition Entry)", it: "Studente (Progetto di Concorso)" } },
      { label: { en: "Location", it: "Location" }, value: "Casamance Region, Senegal" },
      { label: { en: "Typology", it: "Tipologia" }, value: { en: "Healthcare / Humanitarian Architecture", it: "Sanità / Architettura Umanitaria" } },
      { label: { en: "Techniques", it: "Tecniche" }, value: { en: "Compressed Earth Bricks, Bamboo Screens, Passive Ventilation", it: "Mattoni in terra compressa, frangisole in bambù, ventilazione passiva" } }
    ],
    images: generateProjectImages('/003', 6),
    heroImage: '/003/001.webp',
    link: "/project/03",
    challenge: {
      en: "Sub-Saharan healthcare facilities frequently suffer from harsh internal microclimates, high operational costs, and an alienating sterile aesthetic that discourages local women from seeking institutional care during childbirth.",
      it: "Le strutture sanitarie nell'Africa subsahariana soffrono spesso di un microclima interno inadeguato, elevati costi di gestione e un'estetica asettica che allontana le donne della comunità dall'assistenza ospedaliera durante il parto."
    },
    strategy: {
      en: "The design employs locally harvested earth masonry (CSEB), shaded double-roof configurations that induce stack-effect airflow, and landscaped maternal courtyards that embrace vernacular Diola dwelling patterns while providing state-of-the-art hygienic clinical suites.",
      it: "Il progetto utilizza mattoni in terra cruda compressa, coperture doppie ventilate con effetto camino per il raffrescamento passivo e corti verdi che riprendono i modelli insediativi Diola, assicurando al contempo elevati standard igienico-sanitari."
    },
    sections: [
      {
        title: { en: "Bioclimatic Envelope & Passive Cooling", it: "Involucro Bioclimatico e Raffrescamento Passivo" },
        subtitle: { en: "Thermal inertia and airflow without mechanical cooling", it: "Inerzia termica e ventilazione naturale senza climatizzazione attiva" },
        content: {
          en: "Thermal comfort is achieved through thick earth walls providing daytime inertia and night-time heat dissipation. Perforated brick screens (claustra) and bamboo lattices channel trade winds across shaded water basins, dropping interior temperatures by up to 6°C compared to ambient outdoor air.",
          it: "Il comfort termico è ottenuto grazie alle pareti ad alta massa che garantiscono inerzia termica. Le gelosie in mattone e i grigliati in bambù convogliano le correnti d'aria sopra bacini d'acqua ombreggiati, abbattendo la temperatura interna fino a 6°C rispetto all'esterno."
        },
        image: '/003/002.webp',
        imageCaption: {
          en: "Environmental section displaying airflow diagrams and double-skin roof ventilation",
          it: "Sezione ambientale con diagrammi dei flussi d'aria e ventilazione sottotetto"
        }
      },
      {
        title: { en: "Maternal Courtyards & Spatial Privacy", it: "Corti Materne e Privacy Spaziale" },
        subtitle: { en: "Balancing collective solidarity and maternal intimacy", it: "Equilibrio tra supporto comunitario e intimità materna" },
        content: {
          en: "Curved concentric layouts organize the clinic from public arrival zones to intimate post-partum recovery gardens. New mothers rest in rooms open to private gardens where family members can prepare meals and celebrate newborns according to regional traditions.",
          it: "Un impianto curvilineo concentrico ordina la clinica dagli spazi pubblici di accoglienza ai giardini intimi di degenza. Le neo-mamme riposano in stanze affacciate su corti protette dove i familiari possono cucinare e celebrare le nascite secondo le usanze locali."
        },
        image: '/003/004.webp',
        imageCaption: {
          en: "Axonometric breakdown of modular construction steps using local labor",
          it: "Spaccato assonometrico delle fasi costruttive modulari con manodopera locale"
        }
      }
    ]
  },
  {
    id: "04",
    slug: "entanglement-of-obesity-in-the-black-belt",
    title: {
      en: "The Entanglement of Obesity in the Black Belt",
      it: "L'Entanglement dell'Obesità nella Black Belt"
    },
    subtitle: {
      en: "Socio-spatial Urban Analysis of Health, Race, and Capitalism",
      it: "Analisi socio-spaziale e urbana su salute, razza e capitalismo"
    },
    description: {
      en: "The Black Belt, a crescent-shaped region stretching from Alabama to Mississippi, is marked by poverty, race, politics and health challenges. Once fertile, today the region reflects an obesity epidemic due to historical exploitation and capitalism, with roots in slavery and racial inequality.",
      it: "La Black Belt, una regione a forma di mezzaluna che si estende dall'Alabama al Mississippi, è segnata da povertà, razza, politica e sfide sanitarie. Un tempo fertile, oggi la regione riflette un'epidemia di obesità dovuta a sfruttamento storico e capitalismo, con radici nella schiavitù e nelle disuguaglianze razziali."
    },
    category: {
      en: "Urbanism",
      it: "Urbanistica"
    },
    details: [
      { label: { en: "Year", it: "Anno" }, value: "2023" },
      { label: { en: "Role", it: "Ruolo" }, value: { en: "Student (Research & Cartography)", it: "Studente (Ricerca e Cartografia)" } },
      { label: { en: "Location", it: "Location" }, value: "Black Belt, USA" },
      { label: { en: "Typology", it: "Tipologia" }, value: { en: "Spatial Justice / Critical Cartography / Urban Research", it: "Giustizia Spaziale / Cartografia Critica / Ricerca Urbana" } },
      { label: { en: "Institution", it: "Istituzione" }, value: { en: "Polytechnic University of Turin", it: "Politecnico di Torino" } }
    ],
    images: generateProjectImages('/004', 1),
    heroImage: '/004/001.webp',
    link: "/project/04",
    challenge: {
      en: "Decoupling health indicators like obesity from superficial personal-choice narratives, investigating how two centuries of plantation agriculture, infrastructural disinvestment, and discriminatory land practices created systemic 'food deserts'.",
      it: "Disarticolare gli indici sanitari come l'obesità dalla retorica della sola scelta individuale, indagando come due secoli di economia di piantagione, disinvestimento infrastrutturale e politiche fondiarie discriminatorie abbiano creato sistemici 'deserti alimentari'."
    },
    strategy: {
      en: "Through multiscalar GIS mapping, archival correlation, and spatial analysis, the research reconstructs the territorial lineage between rich soil geography, antebellum slavery distribution, modern highway isolation, and chronic nutritional injustice.",
      it: "Attraverso mappature GIS multiscalari, correlazioni d'archivio e analisi territoriale, la ricerca ricostruisce la genealogia spaziale tra conformazione geologica, schiavismo prebellico, isolamento viario contemporaneo e disuguaglianze nutrizionali croniche."
    },
    sections: [
      {
        title: { en: "Soil, Slavery & Systemic Geography", it: "Suolo, Schiavitù e Geografia Sistemica" },
        subtitle: { en: "The dark fertile arc that shaped American capitalism", it: "L'arco fertile e scuro che ha forgiato il capitalismo americano" },
        content: {
          en: "The name 'Black Belt' originally designated the extraordinary strip of dark, rich soil across central Alabama and Mississippi. This geomorphology fueled the highest concentration of enslaved labor in the 19th century. Today, demographic and public health data reveals a mirror imprint: high rates of diabetes, cardiovascular illness, and premature mortality coincide almost exactly with the boundaries of historical plantation counties.",
          it: "Il toponimo 'Black Belt' originariamente indicava la striscia di suolo scuro e ricchissimo tra Alabama e Mississippi. Questa conformazione geomorfologica concentrò la massima presenza di lavoro schiavile nell'Ottocento. Oggi, le mappe demografiche e sanitarie mostrano una sovrapposizione perfetta: i tassi record di obesità, diabete e mortalità precoce ricalcano con impressionante fedeltà i confini delle antiche contee agricole."
        },
        image: '/004/001.webp',
        imageCaption: {
          en: "Comprehensive territorial infographic plate mapping historical agricultural patterns, highway networks, and nutritional deserts",
          it: "Tavola infografica complessa che mappa la storia agricola, l'infrastruttura viaria e la diffusione dei deserti alimentari"
        }
      },
      {
        title: { en: "Spatial Disinvestment & Food Deserts", it: "Disinvestimento Spaziale e Deserti Alimentari" },
        subtitle: { en: "Car dependency and the supermarket redlining crisis", it: "Dipendenza dall'auto e crisi dei presidi di prossimità" },
        content: {
          en: "Modern zoning codes and suburban highway layouts bypassed small unincorporated towns, leaving communities stranded hours away from fresh produce markets. Dollar stores and ultra-processed fast food chains filled the void, converting geographical isolation into an endemic health crisis. The case study advocates for cooperative agricultural infrastructure, regional rail reconnectivity, and municipal land trust stewardship.",
          it: "I codici di zonizzazione moderna e lo sviluppo autostradale hanno emarginato i piccoli centri rurali, isolando intere comunità da mercati ortofrutticoli freschi. Minimarket a basso costo e catene di cibo ultraprocessato hanno colmato il vuoto, cronicizzando la crisi sanitaria. Lo studio propone infrastrutture agricole cooperative, il ripristino di collegamenti regionali e la gestione fiduciaria dei suoli comunali."
        }
      }
    ]
  },
  {
    id: "05",
    slug: "zip-city-europan",
    title: {
      en: "Zip City, Europan",
      it: "Zip City, Europan"
    },
    subtitle: {
      en: "Regeneration of Kop van Isselt Industrial Front",
      it: "Rigenerazione del fronte industriale di Kop van Isselt"
    },
    description: {
      en: "Kop van Isselt emerges as a key site where industrial heritage and a fragmented green-blue network coexist. 'Zip City' envisions a strategic regeneration framework where a new linear park functions like a zipper, linking the Groengordel along Amsterdamseweg with the Eem River. A cycle-pedestrian route and two towers integrating existing industrial sheds define a dynamic urban front and social mixité.",
      it: "Kop van Isselt emerge come sito strategico dove coesistono patrimonio industriale e una rete verde-blu frammentata. 'Zip City' propone un quadro di rigenerazione in cui un nuovo parco lineare funge da cerniera ('zipper'), collegando il Groengordel lungo Amsterdamseweg con il fiume Eem. Un nuovo percorso ciclopedonale e due torri che integrano i capannoni industriali definiscono un fronte urbano dinamico e mixité sociale."
    },
    category: {
      en: "Urbanism / Competition",
      it: "Urbanistica / Concorso"
    },
    details: [
      { label: { en: "Year", it: "Anno" }, value: "2025" },
      { 
        label: { en: "Role", it: "Ruolo" }, 
        value: { 
          en: "Intern at FluidiForme (Design, 3D modelling and rendering)", 
          it: "Stage presso FluidiForme (Design, modellazione 3D e rendering)" 
        } 
      },
      { label: { en: "Location", it: "Location" }, value: "Amersfoort, NL" },
      { label: { en: "Typology", it: "Tipologia" }, value: { en: "Urban Masterplan / Industrial Transformation / Hybrid Towers", it: "Masterplan Urbano / Rigenerazione Industriale / Torri Ibride" } },
      { label: { en: "Studio", it: "Studio" }, value: "FluidiForme" }
    ],
    images: [
      "/005/039_NL_AME_121_front.webp",
      "/005/039_NL_AME_133_back.webp",
      "/005/039_NL_AME_171_orto.webp",
      "/005/039_NL_AME_200_model.webm",
      "/005/039_NL_AME_201_model.webp",
      "/005/039_NL_AME_202.webp",
      "/005/039_NL_AME_203_B.webp",
      "/005/039_NL_AME_203_C.webp",
      "/005/039_NL_AME_203_D.webp"
    ],
    heroImage: "/005/039_NL_AME_121_front.webp",
    link: "/project/05",
    challenge: {
      en: "Kop van Isselt in Amersfoort is historically separated from the riverfront and severed by industrial enclosures, vehicular barriers, and fragmented ecological networks, cutting off city residents from the waterfront.",
      it: "Kop van Isselt ad Amersfoort è storicamente separato dal lungofiume da recinti industriali, barriere veicolari e reti ecologiche frammentate, precludendo ai cittadini il rapporto con l'acqua."
    },
    strategy: {
      en: "The project conceives a linear ecological park acting as an urban zipper ('Zip City'). Industrial sheds are preserved and integrated into the base of two slender mixed-use timber towers, fostering biodiversity corridors, circular production workshops, and civic culture.",
      it: "Il progetto concepisce un parco lineare ecologico che opera come una cerniera urbana ('Zip City'). I capannoni industriali vengono preservati e integrati alla base di due torri ibride a struttura lignea, attivando corridoi di biodiversità, botteghe per la produzione circolare e vita pubblica."
    },
    sections: [
      {
        title: { en: "The Urban Zipper: Linear Park & Eem Waterfront", it: "La Cerniera Urbana: Parco Lineare e Lungofiume Eem" },
        subtitle: { en: "Bridging green corridors and the post-industrial riverbank", it: "Raccordare i corridoi verdi con il lungofiume post-industriale" },
        content: {
          en: "The proposed linear spine connects the green belt along Amsterdamseweg with the river Eem. Bioswales, pedestrian walkways, and cycle superhighways replace impermeable asphalt yards, restoring natural drainage and wildlife transit through the urban tissue.",
          it: "La dorsale lineare proposta connette la cintura verde lungo Amsterdamseweg con il fiume Eem. Bacini di bioritenzione, percorsi ciclopedonali e aree alberate sostituiscono i piazzali asfaltati, ripristinando il drenaggio naturale e la permeabilità ecologica."
        },
        image: "/005/039_NL_AME_171_orto.webp",
        imageCaption: {
          en: "Orthographic masterplan diagram showing continuous green-blue connections and public realm zoning",
          it: "Masterplan ortografico con diagramma delle connessioni verde-blu e della viabilità lenta"
        }
      },
      {
        title: { en: "Hybrid Towers & Industrial Shed Reuse", it: "Torri Ibride e Riuso dei Capannoni" },
        subtitle: { en: "Merging craft production with contemporary high-density housing", it: "Integrare l'artigianato con l'abitare contemporaneo ad alta densità" },
        content: {
          en: "Rather than clearing the site, historic steel sheds are retained to host communal amenities, fabrication laboratories, and indoor public squares. Emerging directly from the industrial plinth, two timber-hybrid towers introduce affordable housing and co-working spaces with panoramic views over Amersfoort.",
          it: "Invece di demolire il costruito esistente, i capannoni in carpenteria metallica vengono preservati per ospitare servizi comuni, laboratori di fabbricazione e piazze coperte. Direttamente dal basamento industriale sorgono due torri ibride in legno per residenze accessibili e spazi di co-working con vista panoramica su Amersfoort."
        },
        image: "/005/039_NL_AME_201_model.webp",
        imageCaption: {
          en: "3D architectural model perspective highlighting the tectonic interface between industrial base and new timber towers",
          it: "Modello 3D che evidenzia l'interfaccia tettonica tra il basamento industriale e le nuove torri in legno"
        }
      }
    ]
  },
  {
    id: "06",
    slug: "uffici-piazza-matteotti",
    title: {
      en: "Piazza Matteotti Offices",
      it: "Uffici Piazza Matteotti"
    },
    subtitle: {
      en: "Executive Office Renovation in Historic Palazzo Matteotti",
      it: "Ristrutturazione uffici direzionali nel Palazzo storico Matteotti"
    },
    description: {
      en: "Developed during an internship at FluidiForme, this project envisions the transformation of a representative floor within a historic building facing Piazza Matteotti in Catanzaro. The proposal investigates layout adaptability (Option 1 and Option 2), redesigns the entrance with vaulted ceilings and mint accents, and defines an elegant materiality of Venetian terrazzo, mermaid quartzite, aluminum cladding, and Prada green upholstery.",
      it: "Sviluppato durante lo stage presso FluidiForme, il progetto affronta la riconfigurazione di un piano di rappresentanza in un palazzo storico affacciato su Piazza Matteotti a Catanzaro. La proposta analizza la flessibilità distributiva (Opzione 1 e Opzione 2), ridisegna l'ingresso voltato con accenti verde menta e definisce una ricercata matericità in seminato veneziano, quarzite mermaid, alluminio e tessile verde Prada."
    },
    category: {
      en: "Interior Design / Heritage",
      it: "Interior Design / Restauro"
    },
    details: [
      { label: { en: "Year", it: "Anno" }, value: "2025" },
      { 
        label: { en: "Role", it: "Ruolo" }, 
        value: { 
          en: "Intern at FluidiForme (Design, 3D modelling and rendering)", 
          it: "Stage presso FluidiForme (Design, modellazione 3D e rendering)" 
        } 
      },
      { label: { en: "Location", it: "Location" }, value: "Catanzaro, IT" },
      { 
        label: { en: "Typology", it: "Tipologia" }, 
        value: { 
          en: "Historic Office Renovation / Workplace", 
          it: "Ristrutturazione Uffici Storici / Workplace" 
        } 
      },
      { label: { en: "Studio", it: "Studio" }, value: "FluidiForme" }
    ],
    images: [
      "/006/036_BCC_CI_060_INGRESSO_scarpa.webp",
      "/006/036_BCC_CI_060_LOBBY.webp",
      "/006/036_BCC_CI_063_OFFICE.webp",
      "/006/036_BCC_CI_068_RIUNIONI.webp",
      "/006/036_BCC_DR_006_OPT1.webp",
      "/006/036_BCC_CI_058_OPT_1_AXO.webp",
      "/006/036_BCC_DR_007_OPT2.webp",
      "/006/036_BCC_CI_059_OPT_2_AXO.webp",
      "/006/036_BCC_DR_008_palette.webp"
    ],
    heroImage: "/006/036_BCC_CI_060_INGRESSO_scarpa.webp",
    link: "/project/06",
    challenge: {
      en: "Upgrading corporate offices inside a historic building in central Catanzaro required addressing rigid compartmentalization, deteriorated interior finishes, and the necessity to balance contemporary corporate technology with the building's historical identity and generous ceiling heights.",
      it: "Riqualificare gli uffici di rappresentanza all'interno di un palazzo storico nel centro di Catanzaro ha richiesto di superare la rigida compartimentazione originaria, il degrado delle finiture interne e la necessità di integrare impianti contemporanei valorizzando le altezze e l'identità dell'edificio storico."
    },
    strategy: {
      en: "Two layout alternatives were developed to maximize spatial flexibility: Option 1 features dedicated executive suites and central meeting rooms, while Option 2 creates expansive fluid workspaces. The entrance is revitalized through soft mint vaults and terrazzo floors, and offices incorporate custom-designed floating acoustic ceiling grids and a refined material board.",
      it: "Due soluzioni distributive sono state elaborate per garantire massima flessibilità: l'Opzione 1 struttura uffici direzionali dedicati e sala meeting centrale, mentre l'Opzione 2 privilegia ampi spazi operativi fluidi. L'atrio viene valorizzato attraverso volte verde menta e pavimento in seminato veneziano, mentre gli uffici integrano controsoffitti metallici modulari sospesi e una ricercata tavolozza materica."
    },
    sections: [
      {
        title: { en: "Spatial Distribution & Comparative Layouts", it: "Distribuzione Spaziale e Layout Comparativi" },
        subtitle: { en: "Flexibility, circulation flows, and 3D axonometric studies", it: "Flessibilità, flussi distributivi e studi assonometrici 3D" },
        content: {
          en: "The project compares two strategic spatial scenarios. Option 1 organizes executive rooms, an 18.5 m² meeting room, entrance lobby, and cafeteria, maintaining distinct privacy gradients. Option 2 introduces an expansive 43 m² open office floor with executive offices overlooking Piazza Matteotti.",
          it: "Il progetto mette a confronto due scenari distributivi. L'Opzione 1 articola uffici direzionali, sala riunioni da 18.5 m², lobby d'ingresso e caffetteria, graduando la privacy. L'Opzione 2 apre a un grande spazio operativo da 43 m² con uffici dirigenziali affacciati direttamente su Piazza Matteotti."
        },
        image: "/006/036_BCC_CI_058_OPT_1_AXO.webp",
        imageCaption: {
          en: "3D axonometric section illustrating interior volumes and spatial zoning",
          it: "Spaccato assonometrico 3D con studio dei volumi interni e zonizzazione funzionale"
        }
      },
      {
        title: { en: "Atmosphere & Architectural Renovation", it: "Atmosfera e Ristrutturazione Architettonica" },
        subtitle: { en: "Dialogue between historical memory and contemporary corporate design", it: "Dialogo tra memoria storica e design contemporaneo per il lavoro" },
        content: {
          en: "The entrance hall is reimagined with a curved linear light installation hugging historic vaults painted in muted sage mint. Within the executive rooms and meeting room, custom suspended open-grid ceilings integrate indirect illumination and acoustic damping without concealing the full ceiling volume.",
          it: "L'atrio d'ingresso viene ripensato con un corpo illuminante continuo che sottolinea le volte a botte in tonalità verde menta. Negli uffici e nella sala riunioni, controsoffitti a griglia metallica aperta accolgono illuminazione indiretta e comfort acustico mantenendo il respiro delle altezze originali."
        },
        image: "/006/036_BCC_CI_060_LOBBY.webp",
        imageCaption: {
          en: "Perspective view of the renovated lobby facing the palm gardens of Piazza Matteotti",
          it: "Vista prospettica della nuova lobby con affaccio sul verde di Piazza Matteotti"
        }
      },
      {
        title: { en: "Materiality & Craftsmanship", it: "Matericità e Dettaglio Costruttivo" },
        subtitle: { en: "Venetian terrazzo, anodized aluminum, and Mermaid quartzite", it: "Seminato veneziano, alluminio anodizzato e quarzite Mermaid" },
        content: {
          en: "A tactile material board brings warmth and prestige: traditional Venetian terrazzo poured and polished on site with marble tesserae, brushed aluminum paneling, natural Mermaid quartzite details, and Prada green fabric accents that evoke Italian mid-century elegance.",
          it: "Una palette materica sofisticata dona calore e prestigio: seminato alla veneziana posato e levigato in opera, pannellature in alluminio satinato, inserti in quarzite Mermaid e rivestimenti tessili in verde Prada che richiamano l'eleganza del design italiano d'autore."
        },
        image: "/006/036_BCC_DR_008_palette.webp",
        imageCaption: {
          en: "Tactile material board: Venetian terrazzo, Mermaid quartzite, textured wall finishes, and textiles",
          it: "Tavola dei materiali: seminato veneziano, quarzite Mermaid, finiture a calce e tessuti"
        }
      }
    ]
  }
];

export const getProjectById = (id: string): Project | undefined => {
  return projectsData.find((p) => p.id === id || p.slug === id);
};
